import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { entryConfig, matchesInvitationPasscode } from "../../config/entry.config";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import nightBackground from "../../assets/entry/starlight-invitation.gif";
import nightPoster from "../../assets/entry/starlight-invitation-still.png";
import "../../styles/birthday-entry.css";

type EntryStage = "locked" | "opening" | "open";

export function BirthdayEntry({ children }: { children: ReactNode }) {
  const [stage, setStage] = useState<EntryStage>("locked");
  const [passcode, setPasscode] = useState("");
  const [showPasscode, setShowPasscode] = useState(false);
  const [backgroundPaused, setBackgroundPaused] = useState(false);
  const [error, setError] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const destination = useRef<HTMLDivElement>(null);
  const submitting = useRef(false);
  const reducedMotion = usePrefersReducedMotion();
  const opening = stage === "opening";

  useEffect(() => {
    if (stage !== "opening") return;
    const timeout = window.setTimeout(() => setStage("open"), reducedMotion ? 0 : 850);
    return () => window.clearTimeout(timeout);
  }, [stage, reducedMotion]);

  useEffect(() => {
    if (stage === "open") {
      destination.current?.querySelector<HTMLButtonElement>(".opening-button")?.focus({ preventScroll: true });
    }
  }, [stage]);

  const unlock = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting.current) return;
    if (!passcode.trim()) {
      setError("请先输入给你的口令。");
      input.current?.focus();
      return;
    }
    if (!matchesInvitationPasscode(passcode)) {
      setError("口令没有对上，再检查一下大小写吧。");
      input.current?.focus();
      input.current?.select();
      return;
    }
    submitting.current = true;
    setPasscode("");
    setShowPasscode(false);
    setError("");
    window.scrollTo({ top: 0, behavior: "instant" });
    setStage("opening");
  };

  return <>
    {stage !== "locked" && (
      <div ref={destination} className="entry-destination" inert={opening} aria-hidden={opening || undefined}>
        {children}
      </div>
    )}
    {stage !== "open" && (
      <main className={`birthday-entry${opening ? " birthday-entry--opening" : ""}`} aria-labelledby="entry-title">
        <picture className="birthday-entry__sky" aria-hidden="true">
          <source media="(prefers-reduced-motion: reduce)" srcSet={nightPoster} />
          <img src={backgroundPaused ? nightPoster : nightBackground} alt="" width="960" height="540" fetchPriority="high" />
        </picture>
        <div className="birthday-entry__shade" aria-hidden="true" />
        <div className="birthday-entry__layout">
          <div className="birthday-entry__content">
            <h1 id="entry-title">{entryConfig.title.map(line => <span key={line}>{line}</span>)}</h1>
            <p className="birthday-entry__intro">{entryConfig.line}</p>
            <form className="invitation-form" onSubmit={unlock} noValidate>
              <label htmlFor="invitation-passcode">专属口令</label>
              <div className={`invitation-form__field${error ? " invitation-form__field--error" : ""}`}>
                <input ref={input} id="invitation-passcode" name="invitation-passcode"
                  type={showPasscode ? "text" : "password"} value={passcode}
                  onChange={event => { setPasscode(event.target.value); setError(""); }}
                  autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                  maxLength={64} required disabled={opening} placeholder="输入给你的口令"
                  aria-invalid={!!error} aria-describedby="invitation-feedback" />
                <button className="invitation-form__visibility" type="button" disabled={opening}
                  aria-label={showPasscode ? "隐藏口令" : "显示口令"}
                  onClick={() => setShowPasscode(value => !value)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path d="M2 12s3.4-6 10-6 10 6 10 6-3.4 6-10 6S2 12 2 12Z" />
                    <circle cx="12" cy="12" r="2.5" />
                    {showPasscode && <path d="m4 3 16 18" />}
                  </svg>
                </button>
              </div>
              <p id="invitation-feedback" className={`invitation-form__feedback${error ? " invitation-form__feedback--error" : ""}`}
                aria-live="polite" aria-atomic="true">
                {error || (opening ? "星光已为你留好，欢迎赴约。" : "输入口令，打开这份生日惊喜。")}
              </p>
              <button className="invitation-form__submit" type="submit" disabled={opening}>
                <span>{opening ? "正在开启…" : entryConfig.action}</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M4 12h15m-6-6 6 6-6 6" />
                </svg>
              </button>
            </form>
          </div>
          {!reducedMotion && <button type="button" className="birthday-entry__motion" disabled={opening}
            onClick={() => setBackgroundPaused(value => !value)}
            aria-label={backgroundPaused ? "播放背景动画" : "暂停背景动画"}>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              {backgroundPaused ? <path d="m7 4 9 6-9 6Z" /> : <path d="M7 4v12m6-12v12" />}
            </svg>
            <span>{backgroundPaused ? "播放背景" : "暂停背景"}</span>
          </button>}
        </div>
      </main>
    )}
  </>;
}
